import { DayHeaderContentArg } from '@fullcalendar/react';
import { FC, useMemo, type JSX } from 'react';

import { format } from 'date-fns';

import './DayHeader.sass';

type Props = {
  dayHeaderContentArg: DayHeaderContentArg;
};
const DayHeader: FC<Props> = ({ dayHeaderContentArg }: Props): JSX.Element => {
  // Month view headers are generic weekday cells anchored to midnight UTC on a
  // reference week, so formatting them locally shifts the label back a day for
  // any viewer behind UTC. Other views receive real local dates.
  const headerDate = useMemo(() => {
    const { date, view } = dayHeaderContentArg;

    if (view.type !== 'dayGridMonth') {
      return date;
    }

    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
  }, [dayHeaderContentArg]);

  const dayOfWeekShort = useMemo(() => {
    return (
      <span className="day-of-week-short">{format(headerDate, 'EEE')}</span>
    );
  }, [headerDate]);

  const dayOfWeekLong = useMemo(() => {
    return (
      <span className="day-of-week-long">{format(headerDate, 'EEEE')}</span>
    );
  }, [headerDate]);

  const dayNumber = useMemo(() => {
    return <span className="day-number">{format(headerDate, 'd')}</span>;
  }, [headerDate]);

  return (
    <div className={`day-header ${dayHeaderContentArg.view.type}`}>
      {['dayGridMonth', 'timeGridWeek'].includes(
        dayHeaderContentArg.view.type
      ) && dayOfWeekShort}
      {['timeGridWeek'].includes(dayHeaderContentArg.view.type) && dayNumber}
      {['timeGridDay'].includes(dayHeaderContentArg.view.type) && dayOfWeekLong}
      {['listWeek'].includes(dayHeaderContentArg.view.type) && (
        <div className="flex space-x-3.5 items-center">
          <span className="flex space-x-3.5 items-center grow">
            {dayOfWeekLong}
          </span>
          <span className="date flex space-x-3.5 items-center">
            {dayNumber}
            {format(headerDate, 'MMM yyyy')}
          </span>
        </div>
      )}
    </div>
  );
};

export default DayHeader;
